import { useState, useEffect, useMemo, useCallback } from 'react';
import SimplePage from './pages/Simple.jsx';
import DetailPage from './pages/Detail.jsx';
import Header from './components/Header.jsx';
import PasswordGate from './components/PasswordGate.jsx';
import { runForecast, summarize, loadInputs, persistInputs, DEFAULTS } from '../engine/index.js';

const MODE_KEY = 'pulsar_mode';

export default function App() {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(MODE_KEY) || 'simple';
    } catch {
      return 'simple';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // localStorage may be disabled (private mode, etc.) — ignore.
    }
  }, [mode]);

  // App-level inputs state — loaded from localStorage on mount (with v1→v2
  // migration) and persisted whenever it changes. Both Simple and Detail
  // pages read from this; the VariableEditor (shipping) writes to it.
  const [inputs, setInputs] = useState(() => loadInputs());

  // Persist on every input change. loadInputs/persistInputs handle storage
  // failures internally so we don't need try/catch here.
  useEffect(() => {
    persistInputs(inputs);
  }, [inputs]);

  const resetInputs = useCallback(() => setInputs(structuredClone(DEFAULTS)), []);

  // Default to the tuned base case: profitable by M33 without changing Pro price.
  // Detail mode exposes ScenarioPills to flip conservative / base / bull.
  const [scenarioKey, setScenarioKey] = useState('base');

  const { rows, summary } = useMemo(() => {
    const r = runForecast(inputs, scenarioKey);
    return { rows: r, summary: summarize(r) };
  }, [inputs, scenarioKey]);

  return (
    <PasswordGate>
      <div className="min-h-screen">
        <Header mode={mode} onModeChange={setMode} inputs={inputs} rows={rows} summary={summary} />
        <main>
          {mode === 'simple' ? (
            <SimplePage inputs={inputs} summary={summary} />
          ) : (
            <DetailPage
              inputs={inputs}
              setInputs={setInputs}
              resetInputs={resetInputs}
              scenarioKey={scenarioKey}
              setScenarioKey={setScenarioKey}
              rows={rows}
              summary={summary}
            />
          )}
        </main>
      </div>
    </PasswordGate>
  );
}
